<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  record: { type: Object, required: true },
  labels: { type: Object, required: true },
  busy: Boolean,
});
const emit = defineEmits(['approve', 'withdraw', 'revise', 'history']);
const note = ref('');
const confirmed = ref(false);
watch(
  () => [props.record.id, props.record.approval_status],
  () => {
    note.value = '';
    confirmed.value = false;
  }
);
const safeUrl = url => (/^https:\/\//i.test(url || '') ? url : undefined);
</script>

<template>
  <article
    class="flex flex-col gap-5 p-6 border rounded-xl bg-n-solid-1 border-n-weak"
  >
    <header>
      <p class="text-xs text-n-slate-11">
        {{ record.record.record_id }} · {{ record.revision }}
      </p>
      <h2 class="mt-1 text-xl font-semibold">{{ record.record.title }}</h2>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ labels[record.approval_status] }}
      </p>
    </header>
    <p class="whitespace-pre-wrap">
      {{ record.record.answer || labels.blocked }}
    </p>
    <section
      v-if="record.record.review_notes"
      class="p-3 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      <h3 class="font-medium">{{ labels.researchNotes }}</h3>
      <p class="mt-2 whitespace-pre-wrap">{{ record.record.review_notes }}</p>
    </section>
    <section v-for="field in ['conditions', 'exceptions']" :key="field">
      <h3 class="font-medium">{{ labels[field] }}</h3>
      <ul class="mt-2 text-sm list-disc ps-5">
        <li v-for="item in record.record[field]" :key="item">{{ item }}</li>
      </ul>
    </section>
    <section>
      <h3 class="font-medium">{{ labels.applicability }}</h3>
      <p class="mt-2 text-sm">
        {{ Object.values(record.record.applicability).flat().join(' · ') }}
      </p>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ labels.validFrom }}: {{ record.record.effective_from || '—' }} ·
        {{ labels.validThrough }}: {{ record.record.valid_through || '—' }}
      </p>
    </section>
    <section>
      <h3 class="font-medium">{{ labels.sources }}</h3>
      <div
        v-for="source in record.record.source_references"
        :key="source.source_id"
        class="p-3 mt-3 border rounded-lg border-n-weak"
      >
        <a
          :href="safeUrl(source.url_or_file)"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm break-all text-n-blue-11"
          >{{ source.url_or_file }}</a
        >
        <p class="mt-2 text-xs text-n-slate-11">{{ source.location }}</p>
        <p class="mt-2 text-sm whitespace-pre-wrap">
          {{ source.supporting_excerpt }}
        </p>
      </div>
    </section>
    <p
      v-if="!record.eligible_for_approval"
      class="p-3 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      {{ labels.blocked }}
    </p>
    <label class="flex flex-col gap-2 text-sm"
      >{{ labels.note
      }}<textarea
        v-model="note"
        rows="3"
        class="p-3 border rounded-lg bg-n-solid-1 border-n-weak"
      />
    </label>
    <label class="flex gap-2 text-sm"
      ><input v-model="confirmed" type="checkbox" />{{ labels.attest }}</label
    >
    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white disabled:opacity-40"
        :disabled="
          busy ||
          !confirmed ||
          !note.trim() ||
          !record.eligible_for_approval ||
          record.approval_status === 'approved'
        "
        @click="emit('approve', note)"
      >
        {{ labels.publish }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm border rounded-lg border-n-weak disabled:opacity-40"
        :disabled="
          busy || !note.trim() || record.approval_status !== 'approved'
        "
        @click="emit('withdraw', note)"
      >
        {{ labels.withdraw }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm border rounded-lg border-n-weak"
        :disabled="busy"
        @click="emit('revise')"
      >
        {{ labels.revise }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm border rounded-lg border-n-weak"
        @click="emit('history')"
      >
        {{ labels.history }}
      </button>
    </div>
    <section v-if="record.review_events.length">
      <h3 class="font-medium">{{ labels.audit }}</h3>
      <p
        v-for="(event, index) in record.review_events"
        :key="index"
        class="mt-2 text-xs text-n-slate-11"
      >
        {{ event.at }} · {{ event.decision }} · {{ event.note }}
      </p>
    </section>
  </article>
</template>
